import {useTranslation} from 'react-i18next'
import {ActivationCounters, ActivationView, isFinalActivationState} from '../../domain/entities/Activation.ts'

// Non-component helpers shared by the activation UI (kept out of the .tsx
// files so React Fast Refresh keeps working).

const DAY_MS = 24 * 60 * 60 * 1000
// "Caducan en 30 días" — same window as activationCounters.expiringIn30d.
export const EXPIRING_SOON_DAYS = 30

export function daysToDeadline(activation: ActivationView, now = Date.now()): number | null {
    if (activation.daysToDeadline != null) return activation.daysToDeadline
    if (activation.redemptionDeadline == null) return null
    return Math.ceil((activation.redemptionDeadline - now) / DAY_MS)
}

export function useActivationFormatters() {
    const {i18n} = useTranslation('dashboard')
    const locale = i18n.language
    return {
        formatDay: (millis: number | null | undefined) => millis == null ? '—'
            : new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC'}).format(new Date(millis)),
        formatDateTime: (millis: number | null | undefined) => millis == null ? '—'
            : new Intl.DateTimeFormat(locale, {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}).format(new Date(millis)),
    }
}

export const touchpointLabelKey = (key: string) => `activation.touchpoint.${key}`

export type ActivationAction = 'editContact' | 'markNoContact' | 'confirmConsent' | 'requestExtra' | 'cancel'

// Which row actions make sense for an activation right now.
export function availableActivationActions(activation: ActivationView): ActivationAction[] {
    if (isFinalActivationState(activation.state)) return []
    const {traveler} = activation
    const actions: ActivationAction[] = ['editContact']
    if (!traveler.noContact) actions.push('markNoContact')
    if (!traveler.noContact && !traveler.consent?.granted) actions.push('confirmConsent')
    // EXTRA is an email to the traveler and needs consent (§4.2): not for route B.
    if (activation.route !== 'B' && traveler.consent?.granted && traveler.consent.channels?.includes('EMAIL')
        && !!traveler.email && traveler.contactStatus !== 'BOUNCED') actions.push('requestExtra')
    actions.push('cancel')
    return actions
}

// Sum of every pack's activationCounters, or null when any pack lacks them
// (older backend): the caller then keeps its client-side count for every pack,
// since that count is pooled and can't be split per pack.
export function sumActivationCounters(counters: (ActivationCounters | null | undefined)[]): ActivationCounters | null {
    if (counters.length === 0 || counters.some((c) => !c)) return null
    return (counters as ActivationCounters[]).reduce((acc, c) => ({
        available: acc.available + c.available,
        assigned: acc.assigned + c.assigned,
        activated: acc.activated + c.activated,
        expiringIn30d: acc.expiringIn30d + c.expiringIn30d,
        expiredReturned: acc.expiredReturned + c.expiredReturned,
    }), {available: 0, assigned: 0, activated: 0, expiringIn30d: 0, expiredReturned: 0})
}
